/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Directory` for the Caintra Connect frontend.
 * @module pages/Directory
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/Directory.module.css";

import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import FeaturedSectors from "../components/FeaturedSectors";
import FeaturedProducts from "../components/FeaturedProducts";
import FeaturedCompanies from "../components/FeaturedCompanies";
import { AppContext } from "../context/AppContext";
import { ReactComponent as DirectoryIcon } from "../assets/icons/Directory.svg";
import { ReactComponent as SearchIcon } from "../assets/icons/Search.svg";
import { ReactComponent as StarIcon } from "../assets/icons/Directory/StarIcon.svg";

import {
  SvgIcon,
  CircularProgress,
  Backdrop,
  Box,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TuneIcon from "@mui/icons-material/Tune";

import { use, useCallback, useMemo, useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { getCompanySizeOptions, getDirectoryFilters } from "../data/selectOptions";
import {
  initializeDirectoryPage,
  logDirectoryInitError,
  resetDirectoryReferenceData,
} from "../logic/directory/directoryInit.js";
import {
  buildDirectorySearchParameters,
  executeDirectorySearch,
  isEmptyDirectorySearch,
  resetDirectorySearchResults,
  EMPTY_DIRECTORY_FILTERS,
} from "../logic/directory/directorySearch.js";
import { writeDirectoryListSession } from "../utils/directory/directoryListSession.js";
import DirectoryCompanyCard from "../components/Directory/DirectoryCompanyCard.jsx";
import {
  getSelectedOption,
  getReferencedDataOptionById,
} from "../utils/common.js";
import { getResultsLimitOptions } from "../utils/resultsLimitOptions.js";
import { canAccessDirectory } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

/**
 * Directory page: company search, filters, and listing.
 * @returns {JSX.Element}
 */
const Directory = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { allowed } = useRequireAccess(canAccessDirectory);

  const resultsOptions = useMemo(
    () => getResultsLimitOptions(t),
    [t, i18n.language],
  );
  const directoryFilters = useMemo(
    () => getDirectoryFilters(t),
    [t, i18n.language],
  );

  const { permissions, changePermissions } = use(AppContext);
  // const [itemsLimit, setItemsLimit] = useState(300);
  const [limitReached, setLimitReached] = useState(false);
  const [initialLastSort, setInitialLastSort] = useState(null); // First sort from which we fetched the companies from OpenSearch
  const [lastSort, setLastSort] = useState(null); // OpenSearch pagination
  const [totalResults, setTotalResults] = useState(0); // Total results from OpenSearch
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [originalCompaniesData, setOriginalCompaniesData] = useState([]);
  const [generalTowns, setGeneralTowns] = useState([]);
  const [towns, setTowns] = useState([]);
  const [states, setStates] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [subSectors, setSubsectors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filteredCompanyData, setFilteredCompanyData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [resultsLimit, setResultsLimit] = useState(resultsOptions[0].value);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [filters, setFilters] = useState({
    sector: null,
    subSector: null,
    rama: null,
    tamanoDeLaEmpresa: null,
    estado: null,
    municipio: null,
    exporta: null,
    importa: null,
  });

  const companySizeOptions = getCompanySizeOptions(t);

  const directorySetters = useMemo(
    () => ({
      setSectors,
      setTowns,
      setGeneralTowns,
      setStates,
      setTotalResults,
      setLastSort,
      setInitialLastSort,
      setLimitReached,
      syncBrowseCompanies: (items) => {
        setFilteredCompanyData(items);
        setOriginalCompaniesData(items);
      },
      applyRestoreState: (restoreState) => {
        setSearchQuery(restoreState.searchQuery);
        setFilters(restoreState.filters);
        setLastSort(restoreState.lastSort);
        setInitialLastSort(restoreState.initialLastSort);
        setFilteredCompanyData(restoreState.filteredCompanyData);
        setOriginalCompaniesData(restoreState.originalCompaniesData);
        setCurrentPage(restoreState.currentPage);
        setResultsLimit(restoreState.resultsLimit);
        setTotalResults(restoreState.totalResults);
        setLimitReached(restoreState.limitReached);
        setSubsectors(restoreState.subSectors);
        setBranches(restoreState.branches);
      },
    }),
    [],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      await initializeDirectoryPage({
        navigate,
        userId: localStorage.getItem("userId"),
        changePermissions,
        setters: directorySetters,
      });
    } catch (err) {
      logDirectoryInitError(err);
      resetDirectoryReferenceData(directorySetters);
    } finally {
      setLoading(false);
    }
  }, [navigate, changePermissions, directorySetters]);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    const token = localStorage.getItem("token");
    if (!!token && token.trim() !== "undefined") {
      fetchData();
    } else {
      navigate("/");
      setLoading(false);
    }
  }, [allowed, fetchData, navigate]);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsDesktop(width >= 1023);
      setIsTablet(width >= 768 && width <= 1022);
      // setIsMediumMobile(width >= 481 && width <= 767);
      // setIsSmallMobile(width < 481);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const getOptionValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }
    return value;
  };

  const getSafeSelectedOption = (options, value) => {
    if (!Array.isArray(options)) {
      logger.warn("Options is not an array:", { data: options });
      return null;
    }
    return getReferencedDataOptionById(options, getOptionValue(value));
  };

  const handleSearch = async (event) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  const handleSearchSubmit = async (
    localFilters = null,
    localSearchQuery = null,
    searchType = "",
  ) => {
    if (localSearchQuery) {
      setSearchQuery(localSearchQuery);
    }
    if (localFilters) {
      setFilters(localFilters);
    }

    const searchParameters = buildDirectorySearchParameters({
      localFilters,
      localSearchQuery,
      searchQuery,
      filters,
      searchType,
    });

    if (isEmptyDirectorySearch(searchParameters)) {
      resetDirectorySearchResults({
        originalCompaniesData,
        setters: {
          setCurrentPage,
          setLastSort,
          setFilteredCompanyData,
          setTotalResults,
        },
      });
      return;
    }

    await executeDirectorySearch({
      navigate,
      searchParameters,
      originalCompaniesData,
      setters: {
        setLastSort,
        setLimitReached,
        setCurrentPage,
        setLoading,
        setTotalResults,
        setFilteredCompanyData,
      },
    });
  };

  const resetFilters = async () => {
    setFilters(EMPTY_DIRECTORY_FILTERS);
    setSubsectors([]);
    setBranches([]);
    setSearchQuery("");
    await handleSearchSubmit(EMPTY_DIRECTORY_FILTERS, "");
  };

  const handleFiltersClick = () => {
    setShowFilters(!showFilters);
  };

  const handleCardClick = (company) => {
    // Save the current state of the directory list
    // This is used to restore the state when returning to the directory page
    writeDirectoryListSession({
      v: 1,
      awaitingRestore: true,
      savedAt: Date.now(),
      searchQuery,
      filters,
      lastSort,
      initialLastSort,
      filteredCompanyData,
      originalCompaniesData,
      currentPage,
      resultsLimit,
      totalResults,
      limitReached,
      subSectors,
      branches,
    });
    navigate("/companyProfile", {
      state: {
        company: company,
        context: "directory",
        selectedProfile:
          company.indicePerfilEncontrado != null
            ? company.indicePerfilEncontrado - 1
            : 0,
      },
    });
  };

  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setLimitReached(false);
    setCurrentPage(1);
  };

  const handlePageChange = async (direction) => {
    // Determine total pages depending on current data source
    const totalPages = Math.ceil(filteredCompanyData.length / resultsLimit);
    if (direction === "next") {
      // Case 1: Still within already fetched data
      if (currentPage < totalPages) {
        setCurrentPage((prevPage) => prevPage + 1);
        return;
      }
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
      setLimitReached(false);
    } else {
      // Unsupported direction or already at bounds. No action required.
    }
  };

  const paginatedCompaniesList = Array.isArray(filteredCompanyData)
    ? filteredCompanyData.slice(
        (currentPage - 1) * resultsLimit,
        currentPage * resultsLimit,
      )
    : [];

  const resultsRangeStart =
    totalResults > 0 ? (currentPage - 1) * resultsLimit + 1 : 0;
  const resultsRangeEnd =
    totalResults > 0 ? Math.min(currentPage * resultsLimit, totalResults) : 0;

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languageSelectorContainer}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <SvgIcon
              sx={{ fontSize: "32px", color: "#1D5AE8" }}
              component={DirectoryIcon}
            />
            <span className={styles.headerTitleText}>
              {t("pages.directory.header")}
            </span>
          </div>
          <div className={styles.searchSection}>
            <div className={styles.searchBarContainer}>
              <div className={styles.searchBar}>
                <div className={styles.searchIconContainer}>
                  <SvgIcon
                    sx={{ fontSize: "16px", color: "#3B3B3D" }}
                    component={SearchIcon}
                  />
                </div>
                <input
                  type="text"
                  placeholder={t("pages.directory.searchText")}
                  value={searchQuery}
                  onChange={handleSearch}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                />
                {searchQuery && (
                  <button
                    className={styles.clearButton}
                    onClick={() => {
                      setSearchQuery("");
                      handleSearchSubmit(null, "");
                    }}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div
                className={styles.searchBarIcon}
                onClick={() => handleSearchSubmit()}
              >
                <SvgIcon
                  sx={{ height: "1.3rem", color: "#FCFCFC" }}
                  component={SearchIcon}
                />
              </div>
            </div>
            <Select
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderRadius: "8px",
                  width: "100%",
                  height: "40px",
                  fontSize: "16px",
                }),
                container: (baseStyles) => ({
                  ...baseStyles,
                  width: !isTablet && !isDesktop ? "100px" : "192px",
                  paddingRight: !isTablet && !isDesktop ? "10px" : "0px",
                  marginLeft: !isTablet && !isDesktop ? "10px" : "0px",
                  // width: "192px",
                }),
                valueContainer: (baseStyles) => ({
                  ...baseStyles,
                }),
                input: (baseStyles) => ({
                  ...baseStyles,
                }),
                indicatorSeparator: () => ({ display: "none" }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  zIndex: 9999,
                  width: !isTablet && !isDesktop ? "150px" : "192px",
                  minWidth: !isTablet && !isDesktop ? "150px" : "192px",
                  right: !isTablet && !isDesktop ? "0" : "auto",
                }),
              }}
              isSearchable={false}
              defaultValue={directoryFilters[0]}
              options={directoryFilters}
              formatOptionLabel={(option) => {
                const showOnlyFlag = !isTablet && !isDesktop;
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                    {option.icon}
                    {!showOnlyFlag && <span>{option.label}</span>}
                  </div>
                );
              }}
            />
            <button
              className={`${styles.advancedFiltersButton} ${
                showFilters ? styles.active : ""
              }`}
              onClick={handleFiltersClick}
            >
              <TuneIcon sx={{ fontSize: 14 }} />
              {isDesktop && t("pages.directory.advancedFilters")}
            </button>
          </div>
          {showFilters && (
            <div className={styles.filtersContainer}>
              <div className={styles.filtersRow}>
                <div className={styles.filterSelect}>
                  <span className={styles.filterLabel}>
                    {t("pages.directory.sector")}
                  </span>
                  <Select
                    options={sectors}
                    isClearable={true}
                    value={getSafeSelectedOption(sectors, filters.sector)}
                    onChange={async (selectedOption) => {
                      let newFilters;
                      if (!selectedOption) {
                        newFilters = {
                          ...filters,
                          sector: null,
                          subSector: null,
                          rama: null,
                        };
                      } else {
                        newFilters = {
                          ...filters,
                          sector: selectedOption._id,
                          subSector: null,
                          rama: null,
                        };
                      }

                      setFilters(newFilters);
                      setSubsectors(selectedOption?.subSectores ?? []);
                      setBranches([]);
                      await handleSearchSubmit(newFilters, null);
                    }}
                    placeholder={t(
                      "components.eventRegistrationModal.sectorPlaceholder",
                    )}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderRadius: "8px",
                        borderColor: filters.sector ? "#1D5AE8" : "#909092",
                        height: "30px",
                        fontSize: "16px",
                        cursor: "pointer",
                        textOverflow: "ellipsis",
                        textWrap: "nowrap",
                        textAlign: "left",
                        maxLines: 1,
                      }),
                      container: (baseStyles) => ({
                        ...baseStyles,
                        width: "100%",
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        // maxWidth: "200px"
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                      }),
                      option: (baseStyles) => ({
                        ...baseStyles,
                        textAlign: "left",
                        cursor: "pointer",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                    getOptionValue={(option) => option.nombre}
                    formatOptionLabel={(option) => <span>{option.nombre}</span>}
                  />
                </div>
                <div className={styles.filterSelect}>
                  <span className={styles.filterLabel}>
                    {t("pages.directory.subsector")}
                  </span>
                  <Select
                    options={subSectors}
                    isSearchable={false}
                    isClearable={true}
                    isDisabled={!filters.sector}
                    value={getSafeSelectedOption(subSectors, filters.subSector)}
                    onChange={async (selectedOption) => {
                      let newFilters;
                      if (!selectedOption) {
                        newFilters = {
                          ...filters,
                          subSector: null,
                          rama: null,
                        };
                      } else {
                        newFilters = {
                          ...filters,
                          subSector: selectedOption._id,
                          rama: null,
                        };
                      }

                      setFilters(newFilters);
                      setBranches(selectedOption?.ramas ?? []);
                      await handleSearchSubmit(newFilters, null);
                    }}
                    placeholder={t("pages.directory.subsectorPlaceholder")}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderRadius: "8px",
                        borderColor: filters.subSector ? "#1D5AE8" : "#909092",
                        height: "30px",
                        fontSize: "16px",
                        cursor: "pointer",
                        textOverflow: "ellipsis",
                        textWrap: "nowrap",
                        textAlign: "left",
                        maxLines: 1,
                      }),
                      container: (baseStyles) => ({
                        ...baseStyles,
                        width: "100%",
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        // maxWidth: "120px"
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                      }),
                      option: (baseStyles) => ({
                        ...baseStyles,
                        cursor: "pointer",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                    getOptionValue={(option) => option.nombre}
                    formatOptionLabel={(option) => <span>{option.nombre}</span>}
                  />
                </div>
                <div className={styles.filterSelect}>
                  <span className={styles.filterLabel}>
                    {t("pages.directory.branch")}
                  </span>
                  <Select
                    options={branches}
                    isSearchable={false}
                    isClearable={true}
                    isDisabled={!filters.subSector}
                    value={getSafeSelectedOption(branches, filters.rama)}
                    onChange={async (selectedOption) => {
                      let newFilters;
                      if (!selectedOption) {
                        newFilters = {
                          ...filters,
                          rama: null,
                        };
                      } else {
                        newFilters = {
                          ...filters,
                          rama: selectedOption._id,
                        };
                      }

                      setFilters(newFilters);
                      await handleSearchSubmit(newFilters, null);
                    }}
                    placeholder={t("pages.directory.branchPlaceholder")}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderRadius: "8px",
                        borderColor: filters.rama ? "#1D5AE8" : "#909092",
                        height: "30px",
                        fontSize: "16px",
                        cursor: "pointer",
                        textOverflow: "ellipsis",
                        textWrap: "nowrap",
                        textAlign: "left",
                        maxLines: 1,
                      }),
                      container: (baseStyles) => ({
                        ...baseStyles,
                        width: "100%",
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        // maxWidth: "120px"
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                      }),
                      option: (baseStyles) => ({
                        ...baseStyles,
                        cursor: "pointer",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                    getOptionValue={(option) => option.nombre}
                    formatOptionLabel={(option) => <span>{option.nombre}</span>}
                  />
                </div>
                <div className={styles.filterSelect2}>
                  <span className={styles.filterLabel}>
                    {t("pages.directory.companySize")}
                  </span>
                  <Select
                    options={companySizeOptions}
                    isSearchable={false}
                    isClearable={true}
                    value={getSelectedOption(
                      companySizeOptions,
                      filters.tamanoDeLaEmpresa,
                    )}
                    onChange={async (selectedOption) => {
                      let newFilters;
                      if (!selectedOption) {
                        newFilters = {
                          ...filters,
                          tamanoDeLaEmpresa: null,
                        };
                      } else {
                        newFilters = {
                          ...filters,
                          tamanoDeLaEmpresa: selectedOption.value,
                        };
                      }

                      setFilters(newFilters);
                      await handleSearchSubmit(newFilters, null);
                    }}
                    placeholder={t("pages.directory.companySizePlaceholder")}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderRadius: "8px",
                        borderColor: filters.tamanoDeLaEmpresa
                          ? "#1D5AE8"
                          : "#909092",
                        height: "30px",
                        fontSize: "16px",
                        cursor: "pointer",
                        textOverflow: "ellipsis",
                        textWrap: "nowrap",
                        textAlign: "left",
                        maxLines: 1,
                      }),
                      container: (baseStyles) => ({
                        ...baseStyles,
                        width: "100%",
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        // maxWidth: "120px"
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                      }),
                      option: (baseStyles) => ({
                        ...baseStyles,
                        cursor: "pointer",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                  />
                </div>
                <div className={styles.filterSelect2}>
                  <span className={styles.filterLabel}>
                    {t("pages.directory.state")}
                  </span>
                  <Select
                    options={states}
                    isSearchable={false}
                    isClearable={true}
                    value={getSafeSelectedOption(states, filters.estado)}
                    onChange={async (selectedOption) => {
                      let newFilters;
                      if (!selectedOption) {
                        newFilters = {
                          ...filters,
                          estado: null,
                          municipio: null,
                        };
                      } else {
                        newFilters = {
                          ...filters,
                          estado: selectedOption._id,
                          municipio: null,
                        };
                        const filteredTowns = generalTowns.filter(
                          (town) => town.idEstado == selectedOption._id,
                        );
                        setTowns(filteredTowns);
                      }

                      setFilters(newFilters);
                      await handleSearchSubmit(newFilters, null);
                    }}
                    placeholder={t("pages.directory.statePlaceholder")}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderRadius: "8px",
                        borderColor: filters.estado ? "#1D5AE8" : "#909092",
                        height: "30px",
                        fontSize: "16px",
                        cursor: "pointer",
                        textOverflow: "ellipsis",
                        textWrap: "nowrap",
                        textAlign: "left",
                        maxLines: 1,
                      }),
                      container: (baseStyles) => ({
                        ...baseStyles,
                        width: "100%",
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        // maxWidth: "120px"
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                      }),
                      option: (baseStyles) => ({
                        ...baseStyles,
                        cursor: "pointer",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                    getOptionValue={(option) => option._id}
                    formatOptionLabel={(option) => <span>{option.nombre}</span>}
                  />
                </div>
                <div className={styles.filterSelect2}>
                  <span className={styles.filterLabel}>
                    {t("pages.directory.town")}
                  </span>
                  <Select
                    options={towns}
                    isSearchable={false}
                    isClearable={true}
                    value={getSafeSelectedOption(towns, filters.municipio)}
                    onChange={async (selectedOption) => {
                      let newFilters;
                      if (!selectedOption) {
                        newFilters = {
                          ...filters,
                          municipio: null,
                        };
                      } else {
                        newFilters = {
                          ...filters,
                          municipio: selectedOption._id,
                        };
                      }

                      setFilters(newFilters);
                      await handleSearchSubmit(newFilters, null);
                    }}
                    placeholder={t("pages.directory.townPlaceholder")}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderRadius: "8px",
                        borderColor: filters.municipio ? "#1D5AE8" : "#909092",
                        height: "30px",
                        fontSize: "16px",
                        cursor: "pointer",
                        textOverflow: "ellipsis",
                        textWrap: "nowrap",
                        textAlign: "left",
                        maxLines: 1,
                      }),
                      container: (baseStyles) => ({
                        ...baseStyles,
                        width: "100%",
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        // maxWidth: "120px"
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                      }),
                      option: (baseStyles) => ({
                        ...baseStyles,
                        cursor: "pointer",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                    getOptionValue={(option) => option._id}
                    formatOptionLabel={(option) => <span>{option.nombre}</span>}
                  />
                </div>
                <div className={styles.filterRadio}>
                  <label>{t("pages.directory.export")}</label>
                  <div className={styles.filterRadioOptions}>
                    <div className={styles.filterRadioOption}>
                      <input
                        type="radio"
                        name="hasExport"
                        checked={filters.exporta === true}
                        onClick={async () => {
                          let newFilters;
                          if (filters.exporta === true) {
                            newFilters = {
                              ...filters,
                              exporta: null,
                            };
                            setFilters(newFilters);
                            await handleSearchSubmit(newFilters, null);
                          }
                        }}
                        onChange={async () => {
                          if (
                            filters.exporta === false ||
                            filters.exporta === null
                          ) {
                            const newFilters = {
                              ...filters,
                              exporta: true,
                            };
                            setFilters(newFilters);
                            await handleSearchSubmit(newFilters, null);
                          }
                        }}
                      />
                      <label>{t("pages.directory.yes")}</label>
                    </div>
                    <div className={styles.filterRadioOption}>
                      <input
                        type="radio"
                        name="hasExport"
                        checked={filters.exporta === false}
                        onClick={async () => {
                          let newFilters;
                          if (filters.exporta === false) {
                            newFilters = {
                              ...filters,
                              exporta: null,
                            };
                            setFilters(newFilters);
                            await handleSearchSubmit(newFilters, null);
                          }
                        }}
                        onChange={async () => {
                          if (
                            filters.exporta === true ||
                            filters.exporta === null
                          ) {
                            const newFilters = {
                              ...filters,
                              exporta: false,
                            };
                            setFilters(newFilters);
                            await handleSearchSubmit(newFilters, null);
                          }
                        }}
                      />
                      <label>{t("pages.directory.no")}</label>
                    </div>
                  </div>
                </div>
                <div className={styles.filterRadio}>
                  <label>{t("pages.directory.import")}</label>
                  <div className={styles.filterRadioOptions}>
                    <div className={styles.filterRadioOption}>
                      <input
                        type="radio"
                        name="hasImport"
                        checked={filters.importa === true}
                        onClick={async () => {
                          let newFilters;
                          if (filters.importa === true) {
                            newFilters = {
                              ...filters,
                              importa: null,
                            };
                            setFilters(newFilters);
                            await handleSearchSubmit(newFilters, null);
                          }
                        }}
                        onChange={async () => {
                          if (
                            filters.importa === false ||
                            filters.importa === null
                          ) {
                            const newFilters = {
                              ...filters,
                              importa: true,
                            };
                            setFilters(newFilters);
                            await handleSearchSubmit(newFilters, null);
                          }
                        }}
                      />
                      <label>{t("pages.directory.yes")}</label>
                    </div>
                    <div className={styles.filterRadioOption}>
                      <input
                        type="radio"
                        name="hasImport"
                        checked={filters.importa === false}
                        onClick={async () => {
                          let newFilters;
                          if (filters.importa === false) {
                            newFilters = {
                              ...filters,
                              importa: null,
                            };
                            setFilters(newFilters);
                            await handleSearchSubmit(newFilters, null);
                          }
                        }}
                        onChange={async () => {
                          if (
                            filters.importa === true ||
                            filters.importa === null
                          ) {
                            const newFilters = {
                              ...filters,
                              importa: false,
                            };
                            setFilters(newFilters);
                            await handleSearchSubmit(newFilters, null);
                          }
                        }}
                      />
                      <label>{t("pages.directory.no")}</label>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <button
                  className={styles.clearFiltersButton}
                  onClick={resetFilters}
                >
                  {t("pages.directory.clearFilters")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Highlights Section */}
      <div className={styles.highlightsContainer}>
        <div className={styles.sectorsContainer}>
          <FeaturedSectors handleSearchAgain={handleSearchSubmit} />
        </div>
        <div className={styles.productsAndCompaniesContainer}>
          <FeaturedProducts handleSearchAgain={handleSearchSubmit} />

          <FeaturedCompanies handleCardClick={handleCardClick} />
        </div>
      </div>

      <div className={styles.main}>
        <div>
          <div className={styles.resultsInfoContainer}>
            <span className={styles.repseLegend}>
              <StarIcon className={styles.repseStarIcon} />
              {t("pages.directory.repseCertificationLegend")}
            </span>
            <span>
              {t("pages.directory.resultsFound", {
                rangeStart: resultsRangeStart,
                rangeEnd: resultsRangeEnd,
                totalResults: totalResults,
              })}
            </span>
          </div>
        </div>
        <div className={styles.companiesList}>
          {paginatedCompaniesList.map((company) => {
            const companySizeSelectedOption = getSelectedOption(
              companySizeOptions,
              company?.tamanoDeLaEmpresa,
            );

            return (
              <DirectoryCompanyCard
                key={company.id}
                company={company}
                companySizeLabel={companySizeSelectedOption?.label || ""}
                states={states}
                towns={towns}
                isDesktop={isDesktop}
                isTablet={isTablet}
                i18n={i18n}
                t={t}
                styles={styles}
                onCardClick={handleCardClick}
              />
            );
          })}
        </div>

        <div className={styles.footerContainer}>
          <div className={styles.footer}>
            {/* Number of results dropdown */}
            <div className="resultsSelector">
              <Select
                options={resultsOptions}
                // defaultValue={resultsOptions[0]}
                value={getSelectedOption(resultsOptions, resultsLimit)}
                isSearchable={false}
                menuPlacement="auto"
                onChange={handleResultsLimitChange}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border: "1px solid #BDBDBD",
                    borderRadius: "8px",
                    padding: "4px 8px",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#888",
                      cursor: "pointer",
                    },
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                    width: "192px",
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                    padding: "0 8px",
                  }),
                  singleValue: (baseStyles) => ({
                    ...baseStyles,
                    color: "#333",
                    fontSize: "16px",
                  }),
                  dropdownIndicator: (baseStyles) => ({
                    ...baseStyles,
                    color: "#333",
                    paddingRight: "8px",
                    "&:hover": {
                      color: "#111",
                    },
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>

            {/* Page selector */}
            <div className={styles.pageSelectorContainer}>
              <div className={styles.pageSelector}>
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton
                    onClick={() => handlePageChange("prev")}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeftIcon />
                  </IconButton>

                  <Typography variant="body1">
                    {t("pages.directory.page")}
                  </Typography>

                  <TextField
                    value={currentPage}
                    size="small"
                    sx={{
                      width: 50,
                      textAlign: "center",
                      backgroundColor: "#fefefe",
                      "& input": { textAlign: "center", padding: "5px 10px" },
                    }}
                    readOnly
                  />

                  <Typography variant="body1">
                    {t("pages.directory.of")}{" "}
                    {Math.ceil(
                      (filteredCompanyData.length || 0) / resultsLimit,
                    )}
                  </Typography>

                  <IconButton
                    onClick={() => handlePageChange("next")}
                    disabled={
                      currentPage ===
                      Math.ceil(
                        (filteredCompanyData.length || 0) / resultsLimit,
                      )
                    }
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={loading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default Directory;
